// @flow
import { Error, VerifiedOutlined } from "@mui/icons-material";
import Box from "@mui/material/Box";
import DialogContentText from '@mui/material/DialogContentText';
import TextField from '@mui/material/TextField';
import Typography from "@mui/material/Typography";
import { FormikProvider, useFormik } from "formik";
import * as React from 'react';
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import ActionStatus from "../../../../../components/app/alerts/actionStatus/actionStatus";
import ConfirmDialog from "../../../../../components/app/dialogs/confirmDialog/confirmDialog";
import { CatalogueLanguages, PartnerTypes, Permissions } from "../../../../../helpers/constants";
import { normalizeGreek } from "../../../../../helpers/normalizeGreek";
import { dialogueContextTextDefaults } from "../../../../../helpers/theme/defaultProps";
import useExhibitionAction from "../../../../../hooks/exhibitions/useExhibitionAction";
import useExhibitionData from "../../../../../hooks/exhibitions/useExhibitionData";
import { EmailValid, useValidateEmail } from "../../../../../hooks/useValidateEmail";
import { EXHIBITOR_PROFILE } from "../../../../../locales/components/namespaces";
import { useHasPermission } from "../../../../../modules/permission/hooks/useHasPermission";

type Props = {
	open: boolean,
	exhibitorId: string,
	exhibitionId: string,
	onClose: Function,
	canAddPartner?: boolean,
	type?: string,
	catalogueLanguage?: boolean,
	addButtonText?: string
}

export default function AddPartnerDialog(props: Props) {
	const { t } = useTranslation(EXHIBITOR_PROFILE)
	const isOrganizer = useHasPermission([Permissions.BeOrganizer], { shouldView: true })
	const { open, onClose, exhibitionId, exhibitorId, type, catalogueLanguage, canAddPartner, addButtonText } = props
	const { validateEmail, emailIsValid, setEmailIsValid } = useValidateEmail()
	const { actionOnPartnerStatus, loading } = useExhibitionData()
	const { createPartner, clearActionOnPartnerStatus } = useExhibitionAction()
	const validationSchema = yup.object({
		name: catalogueLanguage === CatalogueLanguages.EN ? yup.string() : yup.string().required(t('required')),
		nameEn: catalogueLanguage === CatalogueLanguages.EL ? yup.string() : yup.string().required(t('required')).matches(/^([A-Za-z^!@#$%&*()_+\-\\|{}[\].,<>?'":;/`=\u00C0-\u00D6\u00D8-\u00f6\u00f8-\u00ff\s\w]*)$/gi, t('Only latin and special characters allowed.')),
		email: yup
		.string()
		.email(t('Enter a valid email')),
		companyName: catalogueLanguage === CatalogueLanguages.EN ? yup.string().matches(/^([A-Za-z^!@#$%&*()_+\-\\|{}[\].,<>?'":;/`=\u00C0-\u00D6\u00D8-\u00f6\u00f8-\u00ff\s\w]*)$/gi, t('Only latin and special characters allowed.')) : yup.string().notRequired(),
		
	})
	const onSubmit = (values, { resetForm }) => {
		createPartner(exhibitionId, exhibitorId, values, type)
		resetForm()
		setEmailIsValid(EmailValid.Idle)
	}
	const formik = useFormik({
		initialValues: {
			name: '',
			nameEn: '',
			email: '',
			companyName: ''
		},
		validationSchema: validationSchema,
		onSubmit: onSubmit,
	})
	
	const dialogueText = type === PartnerTypes.Partner ? 'Type contact information of the person you want to add as a partner for this event.' :
		type === PartnerTypes.ServiceProvider ? 'Type contact information of the person you want to add as a service provider for this event.' :
			type === PartnerTypes.Contractor ? 'Type contact information of the person you want to add as a contractor for this event.'
				: 'Type contact information of the person you want to add as exhibition staff for this event.'
	
	
	const closeDialogue = () => {
		formik.resetForm();
		onClose();
		setEmailIsValid(EmailValid.Idle)
	}
	
	return (
		<ConfirmDialog
			maxWidth={"sm"}
			loading={loading}
			open={open}
			handleDialog={closeDialogue}
			title={t(addButtonText)}
			otherDialogProps={{ keepMounted: true }}
			yesBtnText={t(addButtonText)}
			onNo={closeDialogue}
			onYes={formik.handleSubmit}
			hasBtnIcons={false}
			disabled={!canAddPartner && !isOrganizer}
		>
			<FormikProvider value={formik}>
				<Box component="form" onSubmit={formik.handleSubmit} noValidate>
					<ActionStatus
						status={actionOnPartnerStatus}
						onClose={clearActionOnPartnerStatus}
					/>
					<DialogContentText
						{...dialogueContextTextDefaults}
					>
						{t(dialogueText)}
					</DialogContentText>
					<Box
						sx={{
							'& .MuiTextField-root': { my: 1 },
						}}>
						{catalogueLanguage !== CatalogueLanguages.EN && (
							<TextField
								fullWidth
								variant={"filled"}
								margin="dense"
								id="name"
								label={t("Name (EL)")}
								value={formik.values.name}
								onBlur={formik.handleBlur}
								onChange={(e) => {
									const oldValue = e.target.value
									formik.setFieldValue('name', normalizeGreek(oldValue.toUpperCase()))
								}}
								required
								error={formik.touched.name && Boolean(formik.errors.name)}
								helperText={formik.touched.name && formik.errors.name}
							/>
						)}
						{catalogueLanguage !== CatalogueLanguages.EL && (
							<TextField
								fullWidth
								variant={"filled"}
								margin="dense"
								label={t("Name (EN)")}
								value={formik.values.nameEn}
								onBlur={formik.handleBlur}
								onChange={(e) => {
									const oldValue = e.target.value
									formik.setFieldValue('nameEn', normalizeGreek(oldValue.toUpperCase()))
								}}
								error={formik.touched.nameEn && Boolean(formik.errors.nameEn)}
								helperText={formik.touched.nameEn && formik.errors.nameEn}
							/>
						)}
						<>
							<Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
								<TextField
									fullWidth
									variant={"filled"}
									margin="dense"
									id="email"
									label={t("Email")}
									type="email"
									value={formik.values.email}
									onBlur={(evt) => {
										formik.handleBlur(evt)
										validateEmail(evt.target.value)
									}}
									onChange={formik.handleChange}
									error={formik.touched.email && Boolean(formik.errors.email)}
									helperText={formik.touched.email && formik.errors.email}
									sx={{
										'& .MuiFilledInput-root': {
											border: emailIsValid === EmailValid.Valid ? '1px ridge #4caf50' : emailIsValid === EmailValid.Error ? '1px ridge #ed6c02' : 'auto',
										}
									}}
								/>
								<Box>
									{emailIsValid === EmailValid.Valid ? (
										<VerifiedOutlined color={'success'} sx={{ marginLeft: 0.5 }}/>
									) : (emailIsValid === EmailValid.Error) ? (
										<Error color={'warning'} sx={{ marginRight: 0.5 }}/>
									) : null}
								</Box>
							</Box>
							{emailIsValid === EmailValid.Error && (
								<Typography variant="body2" sx={{ marginLeft: 1, color: '#ed6c02' }}>
									{t('Email may be incorrect.')}
								</Typography>
							)}
						</>
						{(type === PartnerTypes.ServiceProvider || type === PartnerTypes.ExhibitionStaff || type === PartnerTypes.Contractor) && (
							<TextField
								fullWidth
								variant={"filled"}
								margin="dense"
								id="companyName"
								label={t("Company Name")}
								value={formik.values.companyName}
								onBlur={formik.handleBlur}
								onChange={(e) => {
									const oldValue = e.target.value
									formik.setFieldValue('companyName', normalizeGreek(oldValue.toUpperCase()))
								}}
								error={formik.touched.companyName && Boolean(formik.errors.companyName)}
								helperText={formik.touched.companyName && formik.errors.companyName}
							/>
						)}
					</Box>
					{!canAddPartner && !isOrganizer && (
						<Typography
							variant={'body'}
							color={'error'}
						>
							{t('You reached the limit of partners you can add.')}
						</Typography>
					)}
				</Box>
			</FormikProvider>
		</ConfirmDialog>
	)
}